// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ASK_V2_RAIL_TOGGLE_TITLE_CLASS } from "./AskV2RailSection";

type AskV2RailSubsectionProps = {
  id: string;
  title: string;
  defaultOpen?: boolean;
  /**
   * Bumped by the caller to open this subsection and bring it into view (the
   * thread's "+N related" label). 0 never opens it.
   */
  openSignal?: number;
  children: ReactNode;
};

function scrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function AskV2RailSubsection({
  id,
  title,
  defaultOpen = true,
  openSignal = 0,
  children,
}: AskV2RailSubsectionProps) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(defaultOpen);
  const rootRef = useRef<HTMLDivElement>(null);
  // The signal last scrolled for, so a reader's own collapse and re-expand does not scroll again.
  const scrolledForSignalRef = useRef(0);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  // why: scroll only the nearest scroller (the Context body); `scrollIntoView` would drag the rail and the page with it, as AskV2RailSection notes.
  useEffect(() => {
    const el = rootRef.current;
    if (openSignal === 0 || !open || !el) return;
    if (scrolledForSignalRef.current === openSignal) return;
    const scroller = scrollableAncestor(el);
    if (!scroller) return;
    scrolledForSignalRef.current = openSignal;
    scroller.scrollTop +=
      el.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  }, [openSignal, open]);

  return (
    <div ref={rootRef} data-testid={`ask-v2-rail-subsection-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={t("askV2.rail.toggleSubsection", { section: title })}
        data-testid={`ask-v2-rail-subtoggle-${id}`}
        className="flex w-full items-center gap-2 rounded-md bg-base-white py-1 text-left"
      >
        <Text
          as="span"
          size="1"
          weight="medium"
          className={ASK_V2_RAIL_TOGGLE_TITLE_CLASS}
        >
          {title}
        </Text>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}
